// Admin Orders API service

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://bonbalayelo-api-preview.bonbalayelo.workers.dev';

export interface AdminOrderFilters {
  status?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  minAmount?: number;
  maxAmount?: number;
  sortBy?: 'createdAt' | 'total' | 'orderNumber';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface AdminOrder {
  id: string;
  orderNumber: string;
  memberId: string;
  customer: {
    name: string;
    email: string;
    phone: string;
  };
  status: string;
  paymentMethod?: string;
  paymentStatus: string;
  subtotal: number;
  shippingFee: number;
  discount: number;
  total: number;
  recipient: {
    name: string;
    phone: string;
    address: string;
  };
  notes?: string;
  createdAt: string;
  updatedAt: string;
  paidAt?: string;
  shippedAt?: string;
  deliveredAt?: string;
  cancelledAt?: string;
  cancellationReason?: string;
  items: AdminOrderItem[];
  shipping?: {
    status: string;
    trackingNumber?: string;
    carrier?: string;
    shippedAt?: string;
  };
}

export interface AdminOrderItem {
  id: string;
  orderId: string;
  productId: string;
  variantId?: string;
  variantAttributes?: Record<string, string>;
  productSnapshot: {
    id: string;
    title: string;
    shortDescription?: string;
    sku?: string;
    regularPrice?: number;
    salePrice?: number;
    featuredImageUrl?: string;
    imageUrls?: string[];
  };
  quantity: number;
  price: number;
  subtotal: number;
}

export interface AdminOrdersResponse {
  success: boolean;
  data: AdminOrder[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface AdminOrderDetail extends AdminOrder {
  payment: {
    method?: string;
    status: string;
    amount?: number;
    paidAt?: string;
    transactionId?: string;
  };
}

export interface OrderStats {
  totalOrders: number;
  totalRevenue: number;
  statusBreakdown: Record<string, number>;
  pendingOrders: number;
  shippedOrders: number;
  deliveredOrders: number;
  cancelledOrders: number;
}

/**
 * Get all orders with filtering (admin only)
 */
export async function getAdminOrders(
  filters: AdminOrderFilters = {},
  token: string
): Promise<AdminOrdersResponse> {
  const params = new URLSearchParams();

  if (filters.status) params.append('status', filters.status);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  if (filters.search) params.append('search', filters.search);
  if (filters.minAmount !== undefined) params.append('minAmount', filters.minAmount.toString());
  if (filters.maxAmount !== undefined) params.append('maxAmount', filters.maxAmount.toString());
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
  if (filters.page !== undefined) params.append('page', filters.page.toString());
  if (filters.limit !== undefined) params.append('limit', filters.limit.toString());

  const response = await fetch(`${API_BASE_URL}/api/admin/orders?${params}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch orders');
  }

  return response.json();
}

/**
 * Get order details (admin only)
 */
export async function getAdminOrderById(
  orderId: string,
  token: string
): Promise<{ success: boolean; data: AdminOrderDetail }> {
  const response = await fetch(`${API_BASE_URL}/api/admin/orders/${orderId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch order');
  }

  return response.json();
}

/**
 * Update order status (admin only)
 */
export async function updateOrderStatus(
  orderId: string,
  token: string,
  status: string,
  note?: string
): Promise<{ success: boolean; data: AdminOrderDetail }> {
  const response = await fetch(`${API_BASE_URL}/api/admin/orders/${orderId}/status`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ status, note }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update order status');
  }

  return response.json();
}

/**
 * Update order shipping info (admin only)
 */
export async function updateOrderShipping(
  orderId: string,
  shippingData: {
    status: string;
    trackingNumber?: string;
    shippedAt?: string;
    carrier?: string;
    note?: string;
  },
  token: string
): Promise<{ success: boolean; data: AdminOrderDetail }> {
  const response = await fetch(`${API_BASE_URL}/api/admin/orders/${orderId}/shipping`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(shippingData),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to update shipping info');
  }

  return response.json();
}

/**
 * Get order statistics (admin only)
 */
export async function getOrderStats(
  period: 'day' | 'week' | 'month' | 'year',
  token: string
): Promise<{ success: boolean; data: OrderStats }> {
  const response = await fetch(`${API_BASE_URL}/api/admin/orders/stats/${period}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch order stats');
  }

  return response.json();
}

/**
 * Export orders to CSV
 */
export async function exportOrders(
  token: string,
  filters: AdminOrderFilters = {}
): Promise<void> {
  const params = new URLSearchParams();

  if (filters.status) params.append('status', filters.status);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  if (filters.search) params.append('search', filters.search);
  if (filters.minAmount !== undefined) params.append('minAmount', filters.minAmount.toString());
  if (filters.maxAmount !== undefined) params.append('maxAmount', filters.maxAmount.toString());

  const response = await fetch(`${API_BASE_URL}/api/admin/orders/export?${params}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to export orders');
  }

  // Create download link
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `orders-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}
